<template>
  <transition name="intro-fade">
    <div v-if="showPopup" class="intro-overlay" @click.self="closePopup">
      <transition name="intro-scale">
        <div class="intro-popup">
          <!-- Header -->
          <div class="intro-header">
            <div class="logo-circle">
              <i class="bx bx-bulb"></i>
            </div>
            <h2>Welcome to <span>Julytoss</span></h2>
            <p>Let’s take a quick look at what you can do here.</p>
          </div>

          <!-- Steps -->
          <transition-group name="intro-slide" tag="div" class="intro-content">
            <div
              v-for="(step, i) in steps"
              :key="i"
              v-show="i === currentStep"
              class="intro-step"
            >
              <div class="icon-box">
                <i :class="step.icon"></i>
              </div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </transition-group>

          <!-- Footer -->
          <div class="intro-footer">
            <div class="dots">
              <span
                v-for="(step, i) in steps"
                :key="i"
                :class="['dot', { active: i === currentStep }]"
              ></span>
            </div>

            <div class="buttons" style="margin-top: 1rem;">
              <button
                v-if="currentStep > 0"
                class="btn-secondary"
                @click="prevStep"
              >
                Back
              </button>

              <button
                v-if="currentStep < steps.length - 1"
                class="btn-primary"
                @click="nextStep"
              >
                Next
              </button>

              <button v-else class="btn-black" @click="finishIntro">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from "vue";

const showPopup = ref(false);
const currentStep = ref(0);

const steps = [
  {
    title: "Smart Dashboard",
    desc: "Track invoices, estimates, and business data in one clean dashboard.",
    icon: "bx bx-layout",
  },
  {
    title: "AI-Powered Automation",
    desc: "Use intelligent recommendations to auto-generate documents and follow-ups.",
    icon: "bx bx-brain",
  },
  {
    title: "Client & Payment Tracking",
    desc: "Keep your clients and payments organized — always stay on top of due dates.",
    icon: "bx bx-user-check",
  },
  {
    title: "Secure Cloud Workspace",
    desc: "All your business data, safely encrypted and accessible anywhere.",
    icon: "bx bx-cloud",
  },
];

const nextStep = () => currentStep.value++;
const prevStep = () => currentStep.value--;
const closePopup = () => (showPopup.value = false);

const finishIntro = () => {
  localStorage.setItem("introSeen", "true");
  showPopup.value = false;
};

onMounted(() => {
  if (!localStorage.getItem("introSeen")) {
    setTimeout(() => (showPopup.value = true), 300);
  }
});
</script>

<style scoped>
/* === Overlay === */
.intro-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  z-index: 9999;
  animation: fadeInBg 0.5s ease forwards;
}
@keyframes fadeInBg {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* === Popup Box === */
.intro-popup {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2.5rem 3rem;
  max-width: 620px;
  width: 90%;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.12);
}

/* === Header === */
.intro-header {
  text-align: center;
  margin-bottom: 2rem;
}
.logo-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin: 0 auto 1rem;
  font-size: 2rem;
  animation: floatUp 0.8s ease;
}
@keyframes floatUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.intro-header h2 {
  font-size: 1.7rem;
  color: #111827;
  font-weight: 700;
}
.intro-header p {
  color: #6b7280;
  font-size: 1rem;
}

/* === Content === */
.intro-content {
  text-align: center;
  min-height: 220px;
  position: relative;
}


.icon-box {
  font-size: 2.6rem;
  color: #111827;
  margin-bottom: 1rem;
}

.intro-step h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}
.intro-step p {
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 420px;
  margin: 0 auto;
}

/* === Footer === */
.intro-footer {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dots {
  display: flex;
  gap: 8px;
  margin-bottom: 1rem;
}
.dot {
  width: 10px;
  height: 10px;
  background: #d1d5db;
  border-radius: 50%;
  transition: all 0.3s ease;
}
.dot.active {
  background: #111827;
  transform: scale(1.25);
}
.buttons {
  display: flex;
  gap: 1rem;
}
.btn-primary,
.btn-secondary,
.btn-black {
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary {
  background: #111827;
  color: #fff;
}
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-2px);
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
}
.btn-secondary:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}
.btn-black {
  background: #111827;
  color: #fff;
  font-weight: 600;
}
.btn-black:hover {
  background: #000;
  transform: translateY(-2px);
}

/* === Transitions === */
.intro-fade-enter-active,
.intro-fade-leave-active {
  transition: opacity 0.4s ease;
}
.intro-fade-enter-from,
.intro-fade-leave-to {
  opacity: 0;
}
.intro-scale-enter-active,
.intro-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.intro-scale-enter-from,
.intro-scale-leave-to {
  transform: scale(0.95);
  opacity: 0;
}
.intro-slide-enter-active {
  transition: all 0.4s ease;
}
.intro-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

/* === Responsive === */
@media (max-width: 600px) {
  .intro-popup {
    padding: 2rem 1.5rem;
  }
  .intro-header h2 {
    font-size: 1.4rem;
  }
  .intro-step h3 {
    font-size: 1.1rem;
  }
}
</style>
